import { useEffect, useRef } from 'react';

export interface UseFocusModeProps {
  focusMode: boolean;
  focusedSensors: Record<string, string>;
  enabledSensors: Record<string, Record<string, boolean>>;
  sectionConfigGraphs: any[];
  enableSensor: (sectionType: string, sensorName: string) => void;
  disableSensor: (sectionType: string, sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;
}

/**
 * Manages focus mode save/restore logic
 * When entering focus mode: saves current enabled sensors state and clears loading sensors
 * When exiting focus mode: restores previously saved enabled sensors state
 */
export function useFocusMode({
  focusMode,
  focusedSensors,
  enabledSensors,
  sectionConfigGraphs,
  enableSensor,
  disableSensor,
  clearLoadingSensor,
}: UseFocusModeProps): void {
  // Track previous focus mode to detect transitions
  const previousFocusModeRef = useRef<boolean>(focusMode);
  // Save enabled sensors state before entering focus mode
  const savedEnabledSensorsRef = useRef<Record<string, Record<string, boolean>>>({});

  // Handle focus mode save/restore
  useEffect(() => {
    const wasFocusMode = previousFocusModeRef.current;
    const isFocusMode = focusMode;

    // Entering focus mode - save current enabled state
    if (!wasFocusMode && isFocusMode) {
      // Deep clone the current enabled sensors state
      savedEnabledSensorsRef.current = JSON.parse(JSON.stringify(enabledSensors));

      // CRITICAL: Clear all loading sensors when entering focus mode
      // Focus mode just dims existing data, doesn't fetch new data
      if (clearLoadingSensor) {
        Object.keys(enabledSensors).forEach((sectionId) => {
          Object.keys(enabledSensors[sectionId] || {}).forEach((sensorName) => {
            clearLoadingSensor(sensorName);
          });
        });
      }
    }

    // Exiting focus mode - restore saved state
    if (wasFocusMode && !isFocusMode) {
      // Restore the saved state
      const allSectionTypes: string[] = sectionConfigGraphs.map((g: any) => g.id);
      allSectionTypes.forEach((sectionId: string) => {
        const savedSectionState = savedEnabledSensorsRef.current[sectionId];
        if (savedSectionState) {
          Object.entries(savedSectionState).forEach(([sensorName, wasEnabled]) => {
            const isCurrentlyEnabled = enabledSensors[sectionId]?.[sensorName];
            // Only restore if state is different
            if (wasEnabled && !isCurrentlyEnabled) {
              enableSensor(sectionId, sensorName);
            } else if (!wasEnabled && isCurrentlyEnabled) {
              disableSensor(sectionId, sensorName);
            }
          });
        }
      });

      // Clear saved state
      savedEnabledSensorsRef.current = {};
    }

    // Update ref
    previousFocusModeRef.current = isFocusMode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMode, focusedSensors]);
}
